import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import { BellIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createTripBookingReminder } from '../../../api/trip-bookings/create-trip-booking-reminder'
import { updateTripBookingReminder } from '../../../api/trip-bookings/update-trip-booking-reminder'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TravelReminderTypeEnumMapping } from '../../../lib/types/enums/travel/travel-reminder-type.enum'
import { TripBookingReminder } from '../../../lib/types/models/trip-booking/trip-booking-reminder'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import TripBookingReminderForm from './trip-booking-reminder-form'

type Props = {
  tripBookingId: number
}
const TripBookingRemindersDataTable = ({ tripBookingId }: Props) => {
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    TripBookingReminder
  >(['create', 'edit'])
  const tableRef = React.useRef<DynamicTableRef<TripBookingReminder>>(null)

  const { mutateAsync: createMutation } = useMutation({
    mutationFn: createTripBookingReminder,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Rappel créé avec succès')
    }
  })

  const { mutateAsync: updateMutation } = useMutation({
    mutationFn: updateTripBookingReminder,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Rappel modifié avec succès')
    }
  })

  return (
    <div>
      <NestedDataTable<TripBookingReminder, null>
        ref={tableRef}
        title={'Rappels'}
        variant="action"
        deleteMessage={{
          name: 'rappel',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce rappel ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces rappels ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les rappels ?'
        }}
        columns={[
          {
            accessorKey: 'type',
            header: 'Type de Rappel',
            cell: ({ row }) => TravelReminderTypeEnumMapping[row.original.type]
          },
          {
            accessorKey: 'deadline',
            header: 'Date Limite',
            cell: ({ row }) => (
              <Badge text={DateFormatter.DDMMYYYY(row.original.deadline)} variant="grey" />
            )
          },
          {
            accessorKey: 'daysBeforeDeadline',
            header: 'Jours Avant la Date Limite',
            cell: ({ row }) => (
              <Badge text={row.original.daysBeforeDeadline.toString()} variant="grey" />
            )
          },
          {
            accessorKey: 'comments',
            header: 'Commentaires'
          }
        ]}
        onEditAction={(tripBooking) => toggleModal('edit', tripBooking)}
        endpoint={API['trip-bookings'].getReminders.url.replace(':id', tripBookingId.toString())}
        actions={[
          {
            icon: PlusIcon,
            variant: 'outline',
            label: 'Ajouter un nouveau rappel',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un nouveau rappel'}
        icon={BellIcon}
        hideActions
        onCancel={() => closeModel('create')}
      >
        <TripBookingReminderForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: async (values) => createMutation({ tripBookingId, ...values })
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier le rappel'}
          icon={BellIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <TripBookingReminderForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: async (values) =>
                updateMutation({ id: selectedData.id, tripBookingId, ...values })
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default TripBookingRemindersDataTable
